import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'

export interface DropdownMenuItem {
  id: string
  icon?: ReactNode
  title: string
  description?: string
  onSelect?: () => void
  disabled?: boolean
  keepOpenOnSelect?: boolean
  endSlot?: ReactNode
}

export interface DropdownMenuSection {
  id: string
  title?: string
  items: DropdownMenuItem[]
}

interface DropdownMenuProps {
  label: string
  title?: string
  icon?: ReactNode
  sections: DropdownMenuSection[]
  active?: boolean
  iconOnly?: boolean
  size?: 'sm' | 'md'
  borderless?: boolean
}

interface MenuPosition {
  top: number
  left: number
  visible: boolean
}

export function DropdownMenu({
  label,
  title,
  icon,
  sections,
  active = false,
  iconOnly = false,
  size = 'md',
  borderless = false,
}: DropdownMenuProps) {
  const [open, setOpen] = useState(false)
  const [menuPos, setMenuPos] = useState<MenuPosition | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const menuId = useMemo(
    () => `dropdown-menu-${label.toLowerCase().replace(/\s+/g, '-')}`,
    [label],
  )

  const btnSize = size === 'sm' ? 24 : 30

  function handleToggle() {
    if (!open) {
      const rect = buttonRef.current?.getBoundingClientRect()
      if (rect) {
        // Set a hidden placeholder position; useLayoutEffect will compute the correct one
        setMenuPos({ top: rect.bottom + 8, left: rect.right - 320, visible: false })
      }
    }
    setOpen((v) => !v)
  }

  // After the menu renders (invisible), measure it and snap to the correct position
  useLayoutEffect(() => {
    if (!open || !menuRef.current || !buttonRef.current) return

    const menu = menuRef.current
    const btn = buttonRef.current.getBoundingClientRect()
    const mw = menu.offsetWidth
    const mh = menu.offsetHeight
    const vw = window.innerWidth
    const vh = window.innerHeight
    const pad = 8

    // Vertical: below button, flip above if it overflows the bottom edge
    let top = btn.bottom + pad
    if (top + mh > vh - pad) {
      top = btn.top - mh - pad
    }
    top = Math.max(pad, top)

    // Horizontal: right-align to button by default, flip/shift if it overflows the left edge
    let left = btn.right - mw
    if (left < pad) {
      left = btn.left
    }
    left = Math.max(pad, Math.min(left, vw - mw - pad))

    setMenuPos({ top, left, visible: true })
  }, [open])

  useEffect(() => {
    if (!open) return

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (menuRef.current?.contains(target)) return
      if (rootRef.current?.contains(target)) return
      setOpen(false)
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open])

  const btnClass = `inline-flex items-center rounded-md border text-[11px] font-medium transition-colors ${
    iconOnly ? 'justify-center px-0' : 'justify-between gap-2 px-3'
  }`

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={label}
        title={title ?? label}
        onClick={handleToggle}
        className={btnClass}
        style={{
          height: btnSize,
          width: iconOnly ? btnSize : undefined,
          borderColor: borderless ? 'transparent' : open || active ? 'var(--accent)' : 'var(--border)',
          background: borderless ? 'transparent' : open || active ? 'rgba(99,102,241,.14)' : 'var(--bg-card)',
          color: open || active ? 'var(--accent)' : 'var(--text)',
        }}
      >
        {iconOnly ? (
          <span aria-hidden="true" className="inline-flex items-center justify-center">
            {icon}
          </span>
        ) : (
          <>
            <span className="inline-flex items-center gap-2">
              {icon ? <span aria-hidden="true">{icon}</span> : null}
              <span>{label}</span>
            </span>
            <span aria-hidden="true" className="text-[9px]" style={{ color: 'var(--text-dim)' }}>
              ▾
            </span>
          </>
        )}
      </button>

      {open && menuPos
        ? createPortal(
            <div
              ref={menuRef}
              id={menuId}
              role="menu"
              data-overlay-root="true"
              style={{
                position: 'fixed',
                top: menuPos.top,
                left: menuPos.left,
                visibility: menuPos.visible ? 'visible' : 'hidden',
                zIndex: 9999,
                width: 320,
                borderRadius: 12,
                border: '1px solid var(--border)',
                padding: 8,
                boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
                background: 'var(--bg-popup)',
                color: 'var(--text)',
              }}
            >
              {sections.map((section, sectionIndex) => (
                <div key={section.id}>
                  {section.title ? (
                    <div
                      className="px-3 pb-1 pt-1 text-[9px] uppercase tracking-[0.3px]"
                      style={{ color: 'var(--text-dim)' }}
                    >
                      {section.title}
                    </div>
                  ) : null}
                  <div className="flex flex-col gap-1">
                    {section.items.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-start gap-3 rounded-lg border border-transparent px-3 py-2 text-left transition-colors"
                        style={{ background: 'transparent', color: 'var(--text)' }}
                        onMouseEnter={(event) => {
                          if (item.disabled) return
                          event.currentTarget.style.background = 'rgba(99,102,241,.08)'
                          event.currentTarget.style.borderColor = 'rgba(99,102,241,.18)'
                        }}
                        onMouseLeave={(event) => {
                          event.currentTarget.style.background = 'transparent'
                          event.currentTarget.style.borderColor = 'transparent'
                        }}
                      >
                        <button
                          type="button"
                          role="menuitem"
                          disabled={item.disabled}
                          onClick={() => {
                            if (!item.disabled) item.onSelect?.()
                            if (!item.keepOpenOnSelect) setOpen(false)
                          }}
                          className="flex min-w-0 flex-1 items-start gap-3 text-left disabled:cursor-default disabled:opacity-45"
                          style={{ color: 'inherit' }}
                        >
                          <span
                            aria-hidden="true"
                            className="inline-flex h-4 w-4 items-center justify-center pt-0.5 text-[12px]"
                            style={{ color: 'var(--accent)' }}
                          >
                            {item.icon ?? ''}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[11px]">{item.title}</span>
                            {item.description ? (
                              <span
                                className="mt-0.5 block text-[9px] leading-[1.45]"
                                style={{ color: 'var(--text-dim)' }}
                              >
                                {item.description}
                              </span>
                            ) : null}
                          </span>
                        </button>
                        {item.endSlot}
                      </div>
                    ))}
                  </div>
                  {sectionIndex < sections.length - 1 ? (
                    <div className="my-2 h-px" style={{ background: 'var(--border)' }} />
                  ) : null}
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
